import React, { isValidElement, PureComponent, ReactElement } from 'react';

import { ToastNotificationAPI } from '@philips-emr/tws-components-api';
import { ToastLevel, ToastType } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider, Oswald } from '@philips-emr/tws-core';
import { bool, element, eventListener, expression, number, oneOf, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';
import { ToastProps as ToastServiceProps } from '@philips-emr/tws-core/lib/services/toast-service';
import { classNames, createAbort, enumValues, isEnumValid, isFunction } from '@philips-emr/tws-utils';

import { Link } from '..';

import './toast-notification.css';

const DEFAULT_TIMEOUT = 5000;

const acceptedTypes = [
  ComponentType.NAVIGATION,
];

export interface ToastProps extends Omit<ToastServiceProps, 'action'> {
  children?: ReactElement;
  className?: string;
  uuid: string;
  visible?: boolean;
}

@AwesomeComponent({ type: ComponentType.FEEDBACK, api: ToastNotificationAPI })
class ToastNotification extends PureComponent<ToastProps> {

  private timeoutTimer?: ReturnType<typeof setTimeout>;

  static propTypes = {
    children: element,
    className: string,
    dismissible: bool,
    dismissExpression: expression,
    highlight: bool,
    level: oneOf(enumValues(ToastLevel)),
    message: string,
    onBeforeClose: eventListener,
    onClose: eventListener,
    showIcon: bool,
    timeout: number,
    title: string,
    type: oneOf(enumValues(ToastType)),
    uuid: string,
    visible: bool,
  };

  static defaultProps = {
    dismissExpression: 'tws.toast-notification.dismiss',
    highlight: false,
    level: ToastLevel.INFORMATION,
    type: ToastType.BANNER,
    uuid: '',
    visible: true,
  };

  componentDidMount(): void {
    const { dismissible, timeout } = this.props;

    if (dismissible) {
      return;
    }

    this.timeoutTimer = setTimeout(this.closeToast, timeout ?? DEFAULT_TIMEOUT);
  }

  componentWillUnmount(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
  }

  closeToast = (): void => {
    const { onBeforeClose, onClose } = this.props;

    const abort = createAbort();

    if (isFunction(onBeforeClose)) {
      onBeforeClose(new CloseEvent({ abort: abort }));
    }

    abort.promise.then((aborted) => {
      if (aborted) {
        return;
      }

      if (isFunction(onClose)) {
        onClose(new CloseEvent());
      }
    });
  };

  renderCloseButton(): ReactElement | null {
    const { children, dismissible } = this.props;

    const element = Array.isArray(children) ? children[0] : children;

    if (isValidElement(element) || !dismissible) {
      return null;
    }

    return (
      <a className="c-toast-notification__close" onClick={this.closeToast} />
    );
  }

  renderMessage(): ReactElement {
    const { message } = this.props;

    return (
      <div className="c-toast-notification__message">{MessageService.msg(message)}</div>
    );
  }

  renderTitle(): ReactElement | null {
    const { showIcon, title } = this.props;

    return (
      <div className="c-toast-notification__title-bar">
        {showIcon ? <div className="c-toast-notification__icon" /> : null}
        {title ? <div className="c-toast-notification__title">{MessageService.msg(title)}</div> : null}
      </div>
    );
  }

  renderChildren(): ReactElement | null {
    const { children, dismissible, dismissExpression, uuid } = this.props;

    const Container = ContainerProvider.provide(this);

    const element = Array.isArray(children) ? children[0] : children;

    if (!Container || (!element && !Oswald.isDesigning())) {
      return null;
    }

    return (
      <Container className="c-toast-notification__actions u-expand" acceptedTypes={acceptedTypes} uuid={uuid}>
        {children}
        {dismissible ? <Link className="c-toast-notification__dismiss" onClick={this.closeToast} value={dismissExpression ?? ''} /> : null}
      </Container>
    );
  }

  render(): ReactElement | null {
    const { className, level, highlight, type, visible } = this.props;

    if (!visible) {
      return null;
    }

    const classes = classNames({
      'c-toast-notification': true,
      'c-toast-notification--highlighted': Boolean(highlight),
      [`c-toast-notification--${level}`]: isEnumValid(ToastLevel, level),
      [`c-toast-notification--${type}`]: isEnumValid(ToastType, type),
      [`${className}`]: Boolean(className),
    });

    return (
      <div className={classes}>
        {this.renderTitle()}
        {this.renderMessage()}
        {this.renderChildren()}
        {this.renderCloseButton()}
      </div>
    );
  }
}

export default ToastNotification;
